import { Component, Vue, Inject, Ref, Prop } from 'vue-property-decorator';
import { HistoricoApoyo, mapToHistoricoApoyoModel } from '@/shared/model/historico-apoyos.model';
import { translateFields } from '@/shared/util/translate-fields';
import { EstadoBeca } from '@/shared/model/enumerations/estado-beca.model';
import HistoricoApoyosService from '@/entities/exbecarios/historico-apoyos.service';
import AlertService from '@/shared/alert/alert.service';

/**
 * Componente Vue para la visualización del histórico de apoyos de un solicitante.
 *
 * <p>Permite mostrar en una tabla paginada los apoyos y becas asociados a un CVU,
 * incluyendo información como número de apoyo, convocatoria, tipo, grado,
 * ubicación, estatus, fechas y justificación.</p>
 *
 * @remarks
 * Este componente utiliza los servicios:
 * - {@link HistoricoApoyosService} para obtener los datos desde el backend.
 * - {@link AlertService} para manejar y mostrar errores HTTP.
 *
 * El mapeo de la respuesta del backend se realiza mediante {@link mapToHistoricoApoyoModel}.
 */
@Component
export default class HistoricoApoyosComponent extends Vue {
  /**
   * Servicio para consultar el histórico de apoyos.
   */
  @Inject('historicoApoyosService') private readonly historicoApoyosService!: () => HistoricoApoyosService;

  /**
   * Servicio para mostrar alertas y errores HTTP.
   */
  @Inject('alertService') public alertService!: () => AlertService;

  /**
   * Clave Única del solicitante para filtrar los apoyos.
   */
  @Prop({ required: false })
  public cvu!: string;

  /**
   * Enumeración de estados de beca disponibles.
   */
  readonly EstadoBeca = EstadoBeca;

  /** Filtro de búsqueda aplicado a la tabla. */
  filter = '';

  /** Número de elementos por página en la tabla. */
  perPage = 5;

  /** Página actual de la tabla paginada. */
  currentPage = 1;

  /** Lista de históricos de apoyos a mostrar en la tabla. */
  items: HistoricoApoyo[] = [];

  /** Configuración de columnas para la tabla de históricos de apoyos. */
  fields = [
    { key: 'numeroApoyo', class: 'text-center' },
    { key: 'cvu', class: 'text-center' },
    { key: 'nombreConvocatoria', class: 'text-left', thClass: 'text-center' },
    { key: 'tipo', class: 'text-left', thClass: 'text-center' },
    { key: 'grado', class: 'text-left', thClass: 'text-center' },
    { key: 'ubicacion', class: 'text-left', thClass: 'text-center' },
    { key: 'estatus', class: 'text-center' },
    { key: 'fechaInicio', class: 'text-center' },
    { key: 'fechaTermino', class: 'text-center' },
    { key: 'justificacion', class: 'text-left', thClass: 'text-center' },
    { key: 'origen', class: 'text-center' },
  ];

  /**
   * Ciclo de vida de Vue: llamado cuando el componente es montado.
   *
   * Se encarga de cargar los históricos de apoyos si el CVU está disponible.
   *
   * @async
   * @throws Lanza error si la consulta HTTP falla.
   */
  async mounted() {
    try {
      this.findHistoricoApoyosByCvu();
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
    }
  }

  /**
   * Consulta los históricos de apoyos asociados al CVU proporcionado.
   *
   * @async
   * @returns {Promise<void>} Actualiza la propiedad `items` con los datos obtenidos.
   * @throws Lanza error si la consulta HTTP falla, mostrando alerta.
   */
  async findHistoricoApoyosByCvu() {
    try {
      const historicos = await this.historicoApoyosService().findHistoricoApoyosByCvu(this.cvu);
      this.items = historicos.map(historico => mapToHistoricoApoyoModel(historico));
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }

  /**
   * Formatea una fecha en formato ISO a una representación local legible.
   *
   * @param date Fecha en formato ISO (string) a formatear.
   * @returns {string} Fecha formateada localmente, o cadena vacía si no existe.
   */
  formatDate(date: string): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString();
  }

  /**
   * Getter que retorna las columnas traducidas de la tabla
   * utilizando las claves de internacionalización de la aplicación.
   *
   * @returns {typeof this.fields} Campos de tabla traducidos.
   */
  get translatedFields() {
    return translateFields(this.fields, 'apeironGwApp.historicoApoyos', this.$t.bind(this));
  }
}
